import React from 'react';
import {
  Button,
  Icon,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Checkbox,
  Chip,
  Pagination,
  Typography,
  type Theme
} from '@mui/material';
import Business from '@mui/icons-material/Business';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { ClearFilters, Loader, SearchIconButton } from '../elements';
import { LoaderSize } from '../constants/constants';
import { notifications, extractApiErrorMessage } from '../utils';
import { HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel } from '../api/HealthSystemApi';
import { ApiService } from '../hooks';
import { tokens } from '../theme/tokens';
import { V4_DIALOG_MD, V4_OUTLINED_DEFAULT } from '../theme/v4Compat';

interface IProps {
  selectedSlaughterhouses: HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel[];
  onChange: (slaughterhouses: HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel[]) => void;
  disabled?: boolean;
}

/** A row: rounded and padded, outlined and tinted with the primary colour while selected. */
const rowSx = (selected: boolean) => (theme: Theme) => ({
  borderRadius: '8px',
  p: 1.5,
  // v9 widens the right padding of a row with a secondary action to 48px; v4 let this row's own 12px win.
  '.MuiListItem-root > &': { pr: 1.5 },
  transition: 'background-color 0.2s',
  ...(selected
    ? {
        backgroundColor: theme.palette.primary.light + '20',
        border: `2px solid ${theme.palette.primary.main}`,
        '&:hover': { backgroundColor: theme.palette.primary.light + '30' }
      }
    : { '&:hover': { backgroundColor: theme.palette.action.hover } })
});

const SlaughterhouseSelectModal = React.forwardRef(({ selectedSlaughterhouses, onChange, disabled }: IProps, ref) => {
  const { t } = useTranslation();

  const [slaughterhouses, setSlaughterhouses] = React.useState<
    HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel[]
  >([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const [searchText, setSearchText] = React.useState('');
  const [tempSelected, setTempSelected] = React.useState<
    HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel[]
  >([]);

  const apiService = new ApiService();

  React.useImperativeHandle(ref, () => ({
    openModal() {
      setOpen(true);
    }
  }));

  // pagination
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const slaughterhousesDisplayCount = 8;

  const loadSlaughterhouses = async () => {
    setIsBusy(true);

    apiService.api.slaughterhouses
      .slaughterhousesGetAll({
        Page: page,
        Size: slaughterhousesDisplayCount,
        SearchTerm: searchText,
        IsActive: true
      })
      .then((result) => {
        setSlaughterhouses(result.data.data);
        setTotal(result.data.total);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleSelectorClick = () => {
    setTempSelected([...selectedSlaughterhouses]);
    setOpen(true);
  };

  React.useEffect(() => {
    if (open && !loaded) {
      loadSlaughterhouses();
    }
  }, [open, loaded]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggleSlaughterhouse = (
    slaughterhouse: HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel
  ) => {
    const isSelected = tempSelected.some((s) => s.id === slaughterhouse.id);
    if (isSelected) {
      setTempSelected(tempSelected.filter((s) => s.id !== slaughterhouse.id));
    } else {
      setTempSelected([...tempSelected, slaughterhouse]);
    }
  };

  const handleApply = () => {
    onChange(tempSelected);
    setOpen(false);
  };

  const handleSelectAll = () => {
    setTempSelected([...slaughterhouses]);
  };

  const handleClearAll = () => {
    setTempSelected([]);
  };

  const clearFilters = () => {
    setSearchText('');
    setPage(1);
    setLoaded(false);
  };

  const isSlaughterhouseSelected = (slaughterhouseId: string) => {
    return tempSelected.some((s) => s.id === slaughterhouseId);
  };

  const renderFiltersSection = () => {
    return (
      <div className="py-2">
        {/* v4's spacing-2 row: pulled 8px out on every side, each cell padded 8px; halves split 8:4 from 600px. */}
        <div className="-m-2 box-border flex w-[calc(100%+16px)] flex-wrap items-center">
          <div className="box-border w-full p-2 min-[600px]:w-8/12">
            <TextField
              label={t('f-com-search')}
              variant="outlined"
              size="small"
              fullWidth
              placeholder={t('f-slaughterhouse-search-placeholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: <Icon style={{ marginRight: 8, color: '#9e9e9e' }}>search</Icon>
                }
              }}
            />
          </div>

          <div className="box-border flex w-full justify-end gap-2 p-2 pr-4 min-[600px]:w-4/12">
            <ClearFilters onClick={clearFilters} />
            <SearchIconButton onClick={() => setLoaded(false)} />
          </div>
        </div>
      </div>
    );
  };

  const renderSelectedChips = () => {
    if (selectedSlaughterhouses.length === 0) {
      return <Typography variant="body2">{t('f-slaughterhouse-none-selected')}</Typography>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {selectedSlaughterhouses.map((slaughterhouse) => (
          <Chip
            key={slaughterhouse.id}
            label={slaughterhouse.name}
            size="small"
            color="primary"
            variant="outlined"
            icon={<CheckCircle />}
            sx={{ m: 0.25 }}
          />
        ))}
      </div>
    );
  };

  const getButtonText = () => {
    if (selectedSlaughterhouses.length === 0) {
      return t('f-slaughterhouse-select');
    }
    return `${selectedSlaughterhouses.length} ${t('f-slaughterhouse-selected')}`;
  };

  return (
    <>
      <div>
        <Button
          size="medium"
          variant="outlined"
          color="inherit"
          disabled={disabled}
          endIcon={<Business />}
          onClick={handleSelectorClick}
          fullWidth
          sx={{ ...V4_OUTLINED_DEFAULT, justifyContent: 'space-between' }}
        >
          {getButtonText()}
        </Button>
        {selectedSlaughterhouses.length > 0 && <div className="mt-2">{renderSelectedChips()}</div>}
      </div>

      <Dialog
        onClose={handleClose}
        aria-labelledby="slaughterhouse-select-title"
        maxWidth="md"
        sx={V4_DIALOG_MD}
        fullWidth
        open={open}
      >
        <DialogTitle id="slaughterhouse-select-title" sx={{ background: tokens.sidebar, px: 3, py: 2 }}>
          <div className="flex items-center gap-2">
            <Business color="primary" />
            <Typography variant="h6">{t('f-slaughterhouse-select-modal')}</Typography>
          </div>
        </DialogTitle>

        {/* v9 drops the top padding of content that follows a title; this dialog always had 16px there. */}
        <DialogContent sx={{ px: 3, py: 2, minHeight: 400, '.MuiDialogTitle-root + &': { pt: 2 } }}>
          {renderFiltersSection()}

          <Divider sx={{ my: 2 }} />

          <div className="flex items-center justify-between py-2">
            <Typography variant="body2" color="textSecondary">
              {tempSelected.length} {t('f-com-selected')}
            </Typography>
            <div>
              <Button size="small" color="primary" onClick={handleSelectAll} disabled={!loaded || isBusy}>
                {t('f-com-select-all')}
              </Button>
              <Button
                size="small"
                color="secondary"
                onClick={handleClearAll}
                disabled={tempSelected.length === 0}
                style={{ marginLeft: 8 }}
              >
                {t('f-com-clear-all')}
              </Button>
            </div>
          </div>

          <Divider sx={{ my: 2 }} />

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && slaughterhouses && (
            <List sx={{ maxHeight: 450, overflow: 'auto' }}>
              {slaughterhouses
                .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                .map((slaughterhouse) => {
                  const isSelected = isSlaughterhouseSelected(slaughterhouse.id);
                  return (
                    <ListItem
                      key={slaughterhouse.id}
                      disablePadding
                      // On the row, not its button, so it collapses into the pagination's margin as it did in v4.
                      sx={{ mb: 1 }}
                      secondaryAction={
                        <Checkbox
                          edge="end"
                          checked={isSelected}
                          onChange={() => handleToggleSlaughterhouse(slaughterhouse)}
                          color="primary"
                        />
                      }
                    >
                      <ListItemButton onClick={() => handleToggleSlaughterhouse(slaughterhouse)} sx={rowSx(isSelected)}>
                        <ListItemAvatar>
                          <Avatar
                            sx={
                              isSelected
                                ? { bgcolor: 'primary.main', color: 'primary.contrastText' }
                                : { bgcolor: tokens.sidebar, color: 'text.primary' }
                            }
                          >
                            {slaughterhouse?.name?.[0]?.toUpperCase() || 'S'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {slaughterhouse.name}
                            </Typography>
                          }
                          secondary={
                            <React.Fragment>
                              {slaughterhouse.code && (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  component="span"
                                  sx={{ display: 'block' }}
                                >
                                  {t('f-com-code')}: {slaughterhouse.code}
                                </Typography>
                              )}
                              {slaughterhouse.address && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  component="span"
                                  sx={{ display: 'block' }}
                                >
                                  {slaughterhouse.address}
                                </Typography>
                              )}
                            </React.Fragment>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}

              {slaughterhouses.length > 0 && (
                <ListItem sx={{ justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(total / slaughterhousesDisplayCount)}
                    size="medium"
                    color="primary"
                    showFirstButton
                    showLastButton
                    page={page}
                    onChange={handleChangePage}
                  />
                </ListItem>
              )}

              {slaughterhouses.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-4 p-8">
                  <Business fontSize="large" color="disabled" />
                  <Typography variant="body1" color="textSecondary">
                    {t('f-slaughterhouse-no-results')}
                  </Typography>
                </div>
              )}
            </List>
          )}
        </DialogContent>

        <DialogActions sx={{ background: tokens.sidebar, px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit" sx={{ color: 'text.primary' }}>
            {t('m-global-close')}
          </Button>

          <Button onClick={handleApply} color="primary" variant="contained" disabled={isBusy}>
            {t('f-com-apply')} ({tempSelected.length})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default SlaughterhouseSelectModal;
